import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Sparkles, X, Send, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Any dosing errors this month?",
  "Which technician has the highest patient satisfaction?",
  "Which branch generated the most cross-sell revenue?",
  "Who hasn't logged a cross-sell opportunity?",
];

export default function AIAssistant({ open, onClose }) {
  const { user } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const canSummary = ["super_admin", "ot_head"].includes(user?.app_role);

  useEffect(() => {
    if (open) api.get("/ai/messages").then(({ data }) => setMessages(data)).catch(() => {});
  }, [open]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (q) => {
    const question = q || input;
    if (!question.trim() || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/query", { question });
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) {
      toast.error("AI request failed");
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't process that right now." }]);
    } finally { setBusy(false); }
  };

  const monthlySummary = async () => {
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: "Draft the monthly Founder summary." }]);
    try {
      const { data } = await api.post("/ai/monthly-summary");
      setMessages((m) => [...m, { role: "assistant", text: data.summary }]);
    } catch (e) { toast.error("Summary failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`}>
      <div className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
      <aside className={`absolute right-0 top-0 h-full w-full sm:w-[440px] bg-[#0F172A] border-l border-white/10 flex flex-col transition-transform ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sparkles className="text-[#C9A24B]" size={20} />
            <div>
              <div className="font-heading">OT Team Intelligence</div>
              <div className="text-[10px] text-slate-500">GPT-5.4 · last 30 days context</div>
            </div>
          </div>
          <button data-testid="ai-close-btn" onClick={onClose} className="text-slate-400 hover:text-white"><X /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-slate-400 text-sm">Ask about safety, attendance, satisfaction or cross-sell performance.</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="ai-suggestion" onClick={() => send(s)}
                  className="block w-full text-left text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-2 text-slate-300 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                m.role === "user" ? "bg-[#334155] text-white whitespace-pre-wrap" : "bg-[#131c31] border border-white/10 text-slate-200"}`}>
                {m.role === "user" ? m.text : (
                  <div className="prose-ai space-y-1">
                    <ReactMarkdown components={{
                      h1: ({node, ...p}) => <p className="font-heading text-[#C9A24B] text-base mt-2" {...p} />,
                      h2: ({node, ...p}) => <p className="font-heading text-[#C9A24B] text-sm mt-2" {...p} />,
                      h3: ({node, ...p}) => <p className="font-semibold text-slate-100 mt-2" {...p} />,
                      strong: ({node, ...p}) => <strong className="text-white font-semibold" {...p} />,
                      ul: ({node, ...p}) => <ul className="list-disc pl-4 space-y-0.5" {...p} />,
                      ol: ({node, ...p}) => <ol className="list-decimal pl-4 space-y-0.5" {...p} />,
                      p: ({node, ...p}) => <p className="mb-1" {...p} />,
                    }}>{m.text}</ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Thinking…</div>}
          <div ref={endRef} />
        </div>

        <div className="p-4 border-t border-white/10 space-y-2">
          {canSummary && (
            <button data-testid="ai-summary-btn" onClick={monthlySummary} disabled={busy}
              className="w-full flex items-center justify-center gap-2 text-sm border border-[#C9A24B]/40 text-[#C9A24B] rounded-md py-2 hover:bg-[#C9A24B]/10 transition-colors disabled:opacity-50">
              <FileText size={16} /> Draft Monthly Founder Summary
            </button>
          )}
          <div className="flex items-center gap-2">
            <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question…"
              className="flex-1 bg-[#0b1120] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#C9A24B] focus:outline-none" />
            <button data-testid="ai-send-btn" onClick={() => send()} disabled={busy}
              className="bg-[#C9A24B] text-[#0b1120] rounded-md p-2.5 hover:brightness-110 transition-all disabled:opacity-50"><Send size={18} /></button>
          </div>
        </div>
      </aside>
    </div>
  );
}
